import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import i18n from '../../../i18n';
import { makeStyles } from '@material-ui/core/styles';
import { string } from 'yup';

import { useFormError, validator } from 'core/form/form';

import { isUIEnabledSelector } from 'state/app/selectors';
import { isAuthTemporaryPassSelector, isAuthUserFakeStatusSelector } from 'state/auth/selectors';

import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { LargeButton, LargeButtonColor } from 'components/LargeButton/LargeButton';
import { IGNORE_ERROR, PasswordControlField } from 'components/PasswordControl/PasswordControl';
import { Text, TextTypography } from 'components/Text/Text';

import { PasswordComplexity } from 'pages/Signup/components/PasswordComplexity';

import { Form } from 'forms/types';

import { ReactComponent as LoginSketch } from 'assets/glyphs/check-circle--48.svg';

const formSelector = formValueSelector(Form.FINISH_ONBOARDING_FORM);
const passwordSelector = <T extends {}>(state: T) => formSelector(state, 'password');

export interface FinishOnboardingFormData {
    password: string;
}

export interface FinishOnboardingFormProps {
    onSkip: () => void;
}

const useStyles = makeStyles(theme => ({
    container: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(0, 1),
        [theme.breakpoints.down(640)]: {
            padding: theme.spacing(0, 2),
        },
    },
    item: {
        flexBasis: 'auto',
        margin: theme.spacing(1, 0),
        [theme.breakpoints.down(640)]: {
            textAlign: 'center',
        },
    },
    fullWidthItem: {
        flexBasis: 'auto',
        margin: theme.spacing(1, 0),
        width: '100%',
    },
    blockButton: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
    },
    buttonItem: {
        marginBottom: theme.spacing(3),
        width: '100%',
        '&:first-child': {
            marginRight: theme.spacing(2),
        },
    },
}));

export const FinishOnboardingForm = reduxForm<FinishOnboardingFormData, FinishOnboardingFormProps>({
    form: Form.FINISH_ONBOARDING_FORM,
    initialValues: { password: '' },
    validate: validator({
        password: string()
            .required('common:validation.required')
            .matches(/[a-z]|[а-яё]/, IGNORE_ERROR)
            .matches(/[A-Z]|[А-ЯЁ]/, IGNORE_ERROR)
            .matches(/\d/, IGNORE_ERROR)
            .min(8, IGNORE_ERROR),
    }),
})(props => {
    const { t } = i18n.useTranslation();
    const { handleSubmit, onSkip } = props;

    const error = useFormError(Form.FINISH_ONBOARDING_FORM);

    const isFakeRegistration = useSelector(isAuthUserFakeStatusSelector);

    const isUIEnabled = useSelector(isUIEnabledSelector);

    const password = useSelector(passwordSelector);

    const isTemporaryPass = isFakeRegistration ? false : useSelector(isAuthTemporaryPassSelector);

    const classes = useStyles();
    return (
        <form className={classes.container} onSubmit={handleSubmit}>
            <div>
                <LoginSketch />
            </div>
            <div className={classes.item}>
                <Text typography={TextTypography.TITLE_ACCENT}>{t('onboarding:finish.title')}</Text>
            </div>
            {isTemporaryPass && (
                <Fragment>
                    <div className={classes.item}>
                        <Text typography={TextTypography.PARA}>{t('onboarding:finish.subTitle')}</Text>
                    </div>
                    <div className={classes.fullWidthItem}>
                        <Field name="password" component={PasswordControlField} label={t('auth:signup.password')} />
                    </div>
                    <div className={classes.item}>
                        <PasswordComplexity password={password} />
                    </div>
                </Fragment>
            )}
            {error && (
                <div className={classes.item}>
                    <FormErrorMessage error={error} />
                </div>
            )}
            <div className={classes.item}></div>
            {isTemporaryPass ? (
                <Fragment>
                    <div className={classes.blockButton}>
                        <div className={classes.buttonItem}>
                            <LargeButton color={LargeButtonColor.LIGHT} onClick={onSkip}>
                                {t('onboarding:finish.skip')}
                            </LargeButton>
                        </div>
                        <div className={classes.buttonItem}>
                            <LargeButton color={LargeButtonColor.SUCCESS} disabled={!isUIEnabled} type="submit">
                                {t('onboarding:finish.ready')}
                            </LargeButton>
                        </div>
                    </div>
                </Fragment>
            ) : (
                <div className={classes.fullWidthItem}>
                    <LargeButton color={LargeButtonColor.SUCCESS} onClick={onSkip}>
                        {t('onboarding:finish.ok')}
                    </LargeButton>
                </div>
            )}
        </form>
    );
});
